// 202 -- Following (spectator tab, handoff S13): the schools you follow and
// what's next for each. Anonymous users get the sign-in prompt (this tab is
// the conversion point); signed-in users with no follows get a CTA to
// Preferences (073), which owns follow management. Composed entirely from
// existing endpoints: getPreferences -> getSchool(id) per follow (bounded,
// same pattern as the preferences page) + one discover call scoped to the
// followed schools.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { DiscoverEvent } from "@dugads/shared";
import { Heart, ChevronRight, LogIn } from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { SchoolCrest } from "@/components/school-crest";
import { buttonVariants } from "@/components/ui/button";

interface FollowedSchool {
  id: string;
  name: string;
  logo_url: string | null;
  primary_color: string | null;
}

function fmtWhen(iso: string) {
  return new Date(iso).toLocaleString("en-ZA", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function FollowingPage() {
  const { user } = useAuth();
  const [schools, setSchools] = useState<FollowedSchool[] | null>(null);
  const [events, setEvents] = useState<DiscoverEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const prefs = await api.getPreferences();
        const ids = prefs.followed_school_ids;
        if (cancelled) return;
        if (ids.length === 0) {
          setSchools([]);
          return;
        }
        const [schoolResults, discover] = await Promise.all([
          Promise.all(ids.map((id) => api.getSchool(id).catch(() => null))),
          api.discoverEvents({
            start: new Date().toISOString().slice(0, 10),
            end: new Date(Date.now() + 60 * 86400_000).toISOString().slice(0, 10),
            school: ids.join(","),
            per_page: 50,
          }).catch(() => null),
        ]);
        if (cancelled) return;
        setSchools(
          schoolResults
            .filter((r): r is NonNullable<typeof r> => r !== null)
            .map((r) => ({
              id: r.school.id,
              name: r.school.name,
              logo_url: r.school.logo_url,
              primary_color: r.school.primary_color,
            })),
        );
        setEvents(discover?.events ?? []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Anonymous: the conversion point.
  if (!user) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-12 text-center" data-page="following" data-state="ready" data-view="anonymous">
        <Heart className="h-8 w-8 text-muted-foreground" aria-hidden />
        <h1 className="text-xl font-semibold">Follow your schools</h1>
        <p className="text-sm text-muted-foreground">
          Sign in to follow schools and see their upcoming fixtures here.
        </p>
        <Link to="/login" className={buttonVariants({ size: "sm" })} data-action="following-sign-in">
          <LogIn className="mr-2 h-4 w-4" /> Sign in
        </Link>
      </div>
    );
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="following" data-state="loading">Loading...</p>;
  }

  if (!schools || schools.length === 0) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-12 text-center" data-page="following" data-state="ready" data-view="empty">
        <Heart className="h-8 w-8 text-muted-foreground" aria-hidden />
        <h1 className="text-xl font-semibold">You are not following any schools yet</h1>
        <p className="text-sm text-muted-foreground">
          Follow schools to see their upcoming fixtures in one place.
        </p>
        <Link to="/preferences" className={buttonVariants({ size: "sm" })} data-action="follow-schools">
          Follow schools
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-page="following" data-state="ready">
      <div data-region="header">
        <h1 className="text-2xl font-semibold">Following</h1>
        <p className="text-sm text-muted-foreground">Upcoming fixtures from the schools you follow.</p>
      </div>

      <div className="flex flex-col gap-3" data-region="followed-schools">
        {schools.map((s) => {
          const next = events.filter((e) => e.school_name === s.name).slice(0, 3);
          return (
            <div key={s.id} className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-school-id={s.id}>
              <div className="mb-2 flex items-center gap-2">
                <SchoolCrest name={s.name} logoUrl={s.logo_url} primaryColor={s.primary_color} size={28} />
                <Link to={`/schools/${s.id}`} className="min-w-0 flex-1 truncate text-sm font-semibold hover:underline">
                  {s.name}
                </Link>
              </div>
              {next.length === 0 ? (
                <p className="text-xs text-muted-foreground">No upcoming public events.</p>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {next.map((e) => (
                    <li key={e.id}>
                      <Link
                        to={`/events/${e.id}`}
                        data-event-id={e.id}
                        className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-muted"
                      >
                        <span className="min-w-0 flex-1 truncate">{e.name}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{fmtWhen(e.starts_at)}</span>
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <Link to="/preferences" className="text-sm font-medium text-primary hover:underline" data-action="manage-follows">
        Manage followed schools
      </Link>
    </div>
  );
}
